// ЧАРТ ШАГА 5 — геометрия, маска несущей, бледные источники. B7-2
// `3e619875cbb881ddaae4c782a01d83b2`, решение `s946.d4`.
//
// ИСТОЧНИК — канон `Life Map - s5 Red Line v7.html` (sha256 `6a1fbb7a…`): геометрия `:475–483`,
// `filled()` `:379–386`, `spanD()` `:388–389`, красная и гало в `chartHTML()` `:490–574`.
// ⛔ НЕ §3 `s5-red-line-handoff.md` (спаны 2.8/1.1 + `dasharray 2 2`, кольцо `r 3.4`, пунктир
// источника `1 3`, хедж `too little answered`) — его отменили `s457.d1`/`s662.d2`/v7:234, это
// отставание дока (строка в реестре `WIZARD-CANON.md`). И НЕ кадры раздела D `s5-board-engine.js` —
// LEGACY того же отменённого разреза.
//
// ЧИСТЫЕ ФУНКЦИИ: на входе числа, на выходе числа и строки `d`. Браузер и CSS — экран B7-3.
// Значения несущей считает движок (`src/insights/carrier.js`, `redLine.carrier`) — здесь не
// пересчитываются, только рисуются.

import { fillCarrier } from '../../render/sections/SectionIV.jsx';
import { onePoint } from '../../copy/findingBank.js';
import { LAYERS } from '../../render/layersConfig.js';

// v7 `:475–476`. Высота 182, не 196 из хэндоффа §1: канон — байт v7.
export const CH = Object.freeze({ h: 182, top: 18, bot: 150, left: 16 });
export const AVAIL = 350;
const RIGHT = 10;

// Порог «не влезло» — `fitScroll()` v7 `:640`: разница меньше 8px считается «влезло» (`nofit`).
const FIT_SLACK = 8;
// Высота полосы прокрутки — v7 `:45` (`::-webkit-scrollbar{height:3px}`), бегунок ember.
const EMBER_STRIP = 3;

const clamp = (lo, x, hi) => Math.max(lo, Math.min(hi, x));

/**
 * Геометрия колонок. `colW = clamp(34, (avail − 16 − 10)/N, 92)`, `W = max(avail, 16 + colW·N + 10)`,
 * `originX = max(16, (W − colW·N)/2)` — v7 `:477–482`, те же числа в хэндоффе `:46–53`.
 *
 * Флаги протяжки — когда ряд колонок не влезает в `avail` (правило `fitScroll()` v7; принято
 * web-Claude по `s946.d4`, 30.09). Это НЕ «ровно N=15»: при `avail` 350 ряд перестаёт влезать с N=10
 * (W=366). Замер в тесте.
 */
export function chartGeometry(n, avail = AVAIL) {
  const colW = clamp(34, (avail - CH.left - RIGHT) / n, 92);
  const contentW = colW * n;
  const W = Math.max(avail, CH.left + contentW + RIGHT);
  const originX = Math.max(CH.left, (W - contentW) / 2);
  const overflow = W - avail >= FIT_SLACK;
  return {
    n,
    colW,
    W,
    originX,
    axisX: Math.max(6, originX - 11),
    xMid: (i) => originX + colW * (i + 0.5),
    panX: overflow,
    emberStrip: overflow ? EMBER_STRIP : 0,
  };
}

// v7 `:483`: шкала −3…+3 на полосу top…bot, ноль посередине.
export function yV(v) {
  const mid = (CH.top + CH.bot) / 2;
  return mid - (v / 3) * ((CH.bot - CH.top) / 2);
}

// v7 `filled()` `:379–386`. Интерполяция только для формы — в чтение и доказательства не идёт.
// ⚠️ Совсем пустая несущая даёт нули (плоская линия по оси), а не «линии нет»: так в v7, и
// `s946.d4` п. 5 требует линию при покрытии 0. У паспорта (`fillCarrier`) здесь `null` —
// намеренная разница поверхностей, см. коммент в `SectionIV.jsx`.
export function filledCarrier(carrier) {
  return fillCarrier(carrier) ?? carrier.map(() => 0);
}

// v7 `spanD()` `:388–389` — Catmull-Rom, t = 0.5, один спан на пару соседних колонок.
function spanD(p, i, t = 0.5) {
  const p0 = p[i - 1] || p[i], p1 = p[i], p2 = p[i + 1], p3 = p[i + 2] || p2;
  const f = (x) => x.toFixed(1);
  return `M ${f(p1.x)},${f(p1.y)} C ${f(p1.x + (p2.x - p0.x) * (t / 3))},${f(p1.y + (p2.y - p0.y) * (t / 3))} ${f(p2.x - (p3.x - p1.x) * (t / 3))},${f(p2.y - (p3.y - p1.y) * (t / 3))} ${f(p2.x)},${f(p2.y)}`;
}

/**
 * Один сглаженный путь через все колонки — v7 `smooth()` `:485–489` (Catmull-Rom, t = 0.5), им
 * борд рисует пять не-несущих в кадре 0 reveal (`layerPath()` `:490`, `.rest` `:500`). Геометрия
 * та же, что у `spansD`, но путь один: штрих слоя идёт не обрываясь на стыках колонок.
 */
export function smoothD(values, geom, t = 0.5) {
  const p = values.map((v, i) => ({ x: geom.xMid(i), y: yV(v) }));
  if (p.length < 2) return p.length ? `M ${p[0].x} ${p[0].y}` : '';
  const f = (x) => x.toFixed(1);
  let d = `M ${f(p[0].x)},${f(p[0].y)}`;
  for (let i = 0; i < p.length - 1; i++) {
    const p0 = p[i - 1] || p[i], p1 = p[i], p2 = p[i + 1], p3 = p[i + 2] || p2;
    d += ` C ${f(p1.x + (p2.x - p0.x) * (t / 3))},${f(p1.y + (p2.y - p0.y) * (t / 3))} ${f(p2.x - (p3.x - p1.x) * (t / 3))},${f(p2.y - (p3.y - p1.y) * (t / 3))} ${f(p2.x)},${f(p2.y)}`;
  }
  return d;
}

/** Спаны по значениям без пропусков — красная и бледные источники (v7 `:493`, `:496–497`). */
export function spansD(values, geom) {
  const pts = values.map((v, i) => ({ x: geom.xMid(i), y: yV(v) }));
  const out = [];
  for (let i = 0; i < pts.length - 1; i++) out.push(spanD(pts, i));
  return out.join(' ');
}

/**
 * Маска несущей: ОДИН сплошной путь веса 2.8 по интерполированной форме, гало под ним тем же `d`
 * (v7 `:569–570`). Пунктира, колец и второго «мягкого» пути нет — «красная всегда сплошная»
 * (v7 `:234`); `#redsoft` в v7 оставлен пустым (`d=""`), здесь его нет вовсе.
 * Покрытие 0 и 1 — линия есть, хеджа нет (`s946.d4` п. 5).
 * ⚠️ Перекрыто Н41 (`s950.d1`/`s950.d3`): при ОДНОЙ отвеченной точке линии нет (`d = ''`) — из одной
 * точки она была бы ровной на всю ширину, то есть формой, которой нет. Хеджа по-прежнему нет.
 */
export function redLineMask(carrier, geom = chartGeometry(carrier.length)) {
  const d = onePoint({ carrier }) ? '' : spansD(filledCarrier(carrier), geom);
  return {
    red: { d, strokeWidth: 2.8 },
    glow: { d, strokeWidth: 7, blur: 2.6 },
  };
}

// Потолок бледных источников по конфигурации — v7 `:272`: «одиночка 0, пара 2, тройка 3,
// поле 4–6 все, поле-7 и разброс 0, раскол 1». Набор даёт движок (`carrierSourceLines`), здесь
// только отсечение «все семь» (они дублировали бы §III — то же правило, что у паспорта).
export const SOURCE_CEILING = Object.freeze({ singleton: 0, pair: 2, triplet: 3, field: 6, scatter: 0, split: 1 });
const ALL_SEVEN = 7;

const DASH = Object.fromEntries(LAYERS.map((L) => [L.key, L.dash ?? null]));

/**
 * Бледные источники: сплошные 1.2 (v7 `:498`). Своего пунктира у источника нет (`1 3` отменён);
 * штрих, если есть, — identity-dash слоя (`s585.d1`), тот же, что в паспорте.
 */
export function paleSources(sourceLines) {
  if (sourceLines.length >= ALL_SEVEN) return [];
  return sourceLines.map((k) => ({ key: k, strokeWidth: 1.2, dash: DASH[k] }));
}
